import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import {
  TwDropdownComponent,
  TwDropdownItemComponent,
  TwDropdownMenuComponent,
  TwDropdownTriggerDirective,
} from '@twist/ui-popovers';

/**
 * A single overflow-menu action.
 *
 * Mirrors the legacy `UiActionListItem` (`page-header.models.ts`) shape but
 * lives in `@twist/ui-primitives` so the page-header family does not import
 * from `@twist/ui/components` (which would close a project-graph cycle).
 * `icon` is an icon-ref string (resolved by the host's icon system) rather
 * than the legacy `SvgIcons` union. Rendered through the `tw-dropdown`
 * family — never ng-bootstrap.
 */
export interface TwPageHeaderAction {
  /** Menu row label. */
  label: string;
  /** Click handler. */
  action?: () => void;
  /** Router link (forwarded to `routerLink`). */
  routerLink?: string | unknown[];
  /** External href. */
  href?: string;
  /** Hide the item when `false`. */
  visible?: boolean;
  /** Disable the item (no-op on activation). */
  disabled?: boolean;
  /** Optional icon-ref rendered before the label. */
  icon?: string;
  /** Optional trailing hint text (e.g. a shortcut). */
  hint?: string;
  /**
   * Destructive item — requires a confirming second click. The first click
   * arms the item (label swaps to "Click again to confirm"); the second
   * click fires `action` / emits `select`.
   */
  danger?: boolean;
}

/** Divider form of an overflow-menu entry. */
export interface TwPageHeaderActionDivider {
  divider: true;
  visible?: boolean;
}

/** Union of the two overflow-menu entry forms. */
export type TwPageHeaderActionItem = TwPageHeaderAction | TwPageHeaderActionDivider;

/**
 * `tw-page-header-actions` — hybrid action region for {@link
 * import('./tw-page-header.component').TwPageHeaderComponent}.
 *
 * Two complementary mechanisms on the same component:
 *
 * 1. **Declarative `[actions]` overflow** — a `TwPageHeaderActionItem[]`
 *    rendered as a `⋮` overflow menu through the modern `tw-dropdown`
 *    family (native `[popover]` + CDK fallback, full WAI-ARIA keyboard
 *    nav). Supports dividers, per-item icons, optional hint text, and the
 *    **danger "click again to confirm"** inline pattern for destructive
 *    items. The overflow `⋮` trigger is not rendered when there are no
 *    visible non-divider items.
 * 2. **Projection slot** — the default `<ng-content>` carries primary
 *    buttons / custom controls (a `tw-btn`, a `ui-version-switcher`, a
 *    `ui-version-lifecycle-bar`, a `tw-segmented`, …) before the overflow.
 *
 * Note: most pages project their primary buttons into the **title's**
 * `[slot=actions]` slot (so they sit on the title row). This component's
 * own grid cell is reserved for the standalone declarative overflow menu;
 * projection here is available for pages that want a separate action band.
 *
 * Styling lives in the parent `tw-page-header.component.scss` via the
 * `.tw-page-header-actions*` BEM classes.
 */
@Component({
  selector: 'tw-page-header-actions',
  standalone: true,
  imports: [
    TwDropdownComponent,
    TwDropdownTriggerDirective,
    TwDropdownMenuComponent,
    TwDropdownItemComponent,
  ],
  templateUrl: './tw-page-header-actions.component.html',
  host: {
    'class': 'tw-page-header-actions',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TwPageHeaderActionsComponent {
  /** Declarative overflow menu items. Empty / all-hidden → no `⋮` trigger. */
  readonly actions = input<TwPageHeaderActionItem[]>([]);

  /** Accessible name for the overflow trigger. */
  readonly overflowLabel = input<string>('More actions');

  /** Emitted with the activated action when a (non-divider) item fires. */
  readonly actionSelect = output<TwPageHeaderAction>();

  /** Index (within `visibleItems`) of the currently-armed danger item, or -1. */
  protected readonly armedIndex = signal(-1);

  /** Controlled open state — lets us re-open after arming a danger item. */
  protected readonly open = signal(false);

  /** Visible entries (dividers always count toward layout, others gated by `visible`). */
  protected readonly visibleItems = computed<TwPageHeaderActionItem[]>(() =>
    this.actions().filter((it) => this.isDivider(it) || it.visible !== false),
  );

  /** Whether to render the overflow `⋮` trigger at all. */
  protected readonly hasOverflow = computed(() =>
    this.visibleItems().some((it) => !this.isDivider(it)),
  );

  protected isDivider(it: TwPageHeaderActionItem): it is TwPageHeaderActionDivider {
    return (it as TwPageHeaderActionDivider).divider === true;
  }

  protected onOpenChange(next: boolean): void {
    this.open.set(next);
    // Clear the armed danger state whenever the menu closes.
    if (!next) {
      this.armedIndex.set(-1);
    }
  }

  /**
   * Item activation. `tw-dropdown-item` auto-closes the menu after
   * `(selected)` fires, so for the first click on a danger item we
   * re-open the menu (via the controlled `open` signal) on the next
   * microtask to keep the armed "click again to confirm" state visible.
   */
  protected onItemSelected(item: TwPageHeaderAction, index: number): void {
    if (item.disabled) return;
    if (item.danger && this.armedIndex() !== index) {
      this.armedIndex.set(index);
      queueMicrotask(() => this.open.set(true));
      return;
    }
    this.armedIndex.set(-1);
    item.action?.();
    this.actionSelect.emit(item);
  }

  /** Label shown for an item (swaps to the confirm prompt when armed). */
  protected labelFor(item: TwPageHeaderAction, index: number): string {
    return item.danger && this.armedIndex() === index
      ? 'Click again to confirm'
      : item.label;
  }
}
